import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Box,
  Collapse,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  GridItem,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Textarea,
  List,
  ListItem,
  Button,
} from '@chakra-ui/react'
import { searchStores } from '@/lib/services/ticketRequestService'
import { isOnsiteServiceParent, type TicketFormOption } from '@/types/ticketRequest'
import { validateAvailabilityRange } from '@/lib/voiceAi/buildVoiceAiCallPayload'
import { useTicketOptions } from '@/hooks/useTicketOptions'

export type TriageFormState = {
  storeId: string
  storeCode: string
  storeLabel: string
  siteAddress: string
  errorMessage: string
  preferredScheduleUntil: string
  contactAvailabilityText: string
  workImpact: string
  othersAffected: string
  userScope: string
  appScope: string
  accessType: string
  workPermitNeeded: string
  permitAvailable: string
  permitDuration: string
  partsApproved: string
  closeOnFix: string
  triageExpanded: boolean
}

type Props = {
  serviceParents: TicketFormOption[]
  serviceParent: string
  preferredScheduleAt: string
  triage: TriageFormState
  onChange: (patch: Partial<TriageFormState>) => void
  scheduleRangeError?: string | null
}

function RadioRow({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: string[]
  onChange: (v: string) => void
}) {
  return (
    <FormControl>
      <FormLabel fontSize="sm">{label}</FormLabel>
      <RadioGroup value={value || ''} onChange={onChange}>
        <Stack direction="row" spacing={4} flexWrap="wrap">
          {options.map((opt) => (
            <Radio key={opt} value={opt} size="sm">
              {opt}
            </Radio>
          ))}
        </Stack>
      </RadioGroup>
    </FormControl>
  )
}

export function TicketFormTriageSection({
  serviceParents,
  serviceParent,
  preferredScheduleAt,
  triage,
  onChange,
  scheduleRangeError,
}: Props) {
  const { codes: othersAffectedOptions } = useTicketOptions('others_affected')
  const { codes: userScopeOptions } = useTicketOptions('user_scope')
  const { codes: appScopeOptions } = useTicketOptions('app_scope')
  const { codes: accessTypeOptions } = useTicketOptions('access_type')
  const { codes: workPermitNeededOptions } = useTicketOptions('work_permit_needed')
  const { codes: permitAvailableOptions } = useTicketOptions('permit_available')
  const { codes: partsApprovedOptions } = useTicketOptions('parts_approved')
  const { codes: closeOnFixOptions } = useTicketOptions('close_on_fix')
  const showOnsite = isOnsiteServiceParent(serviceParents, serviceParent)
  const [storeQuery, setStoreQuery] = useState(triage.storeLabel || triage.storeCode || '')
  const [storeResults, setStoreResults] = useState<
    Array<{ id: number; branch_code: string; branch_name: string; branch_address?: string | null }>
  >([])
  const [storeLoading, setStoreLoading] = useState(false)
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const runStoreSearch = useCallback(async (q: string) => {
    if (q.trim().length < 3) {
      setStoreResults([])
      return
    }
    setStoreLoading(true)
    try {
      const results = await searchStores(q)
      setStoreResults(results)
    } catch {
      setStoreResults([])
    } finally {
      setStoreLoading(false)
    }
  }, [])

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => {
      void runStoreSearch(storeQuery)
    }, 350)
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current)
    }
  }, [storeQuery, runStoreSearch])

  const localScheduleError =
    scheduleRangeError ??
    validateAvailabilityRange(preferredScheduleAt, triage.preferredScheduleUntil)

  return (
    <Box borderWidth="1px" borderRadius="md" p={3} bg="gray.50">
      <Button
        variant="ghost"
        size="sm"
        width="100%"
        justifyContent="space-between"
        onClick={() => onChange({ triageExpanded: !triage.triageExpanded })}
        fontWeight="600"
        color="blue.900"
      >
        Help us route your request faster
        <Text as="span" fontSize="xs" color="gray.500">
          {triage.triageExpanded ? 'Hide' : 'Show'} optional details
        </Text>
      </Button>

      <Collapse in={triage.triageExpanded} animateOpacity>
        <Grid templateColumns="1fr" gap={3} mt={3}>
          <GridItem>
            <FormControl>
              <FormLabel fontSize="sm">Exact error message (optional)</FormLabel>
              <Input
                value={triage.errorMessage}
                onChange={(e) => onChange({ errorMessage: e.target.value })}
                placeholder="e.g. Paper jam error E3"
                maxLength={200}
              />
              <FormHelperText>Short error text ALICE can confirm on the call.</FormHelperText>
            </FormControl>
          </GridItem>

          <GridItem>
            <FormControl>
              <FormLabel fontSize="sm">Dispatch until</FormLabel>
              <Input
                type="datetime-local"
                value={triage.preferredScheduleUntil}
                onChange={(e) => onChange({ preferredScheduleUntil: e.target.value })}
              />
              {localScheduleError && (
                <FormHelperText color="red.500">{localScheduleError}</FormHelperText>
              )}
            </FormControl>
          </GridItem>

          <GridItem>
            <FormControl>
              <FormLabel fontSize="sm">Preferred contact window (optional)</FormLabel>
              <Input
                value={triage.contactAvailabilityText}
                onChange={(e) => onChange({ contactAvailabilityText: e.target.value })}
                placeholder="e.g. Weekdays 9 AM – 5 PM"
              />
            </FormControl>
          </GridItem>

          {(showOnsite || triage.storeCode || triage.siteAddress) && (
            <>
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm">Store / site</FormLabel>
                  <Input
                    value={storeQuery}
                    onChange={(e) => {
                      setStoreQuery(e.target.value)
                      onChange({ storeLabel: e.target.value, storeCode: '', storeId: '' })
                    }}
                    placeholder="Search by store name or code (min 3 characters)"
                  />
                  {storeLoading && (
                    <FormHelperText>Searching stores...</FormHelperText>
                  )}
                  {storeResults.length > 0 && (
                    <List
                      mt={1}
                      borderWidth="1px"
                      borderRadius="md"
                      bg="white"
                      maxH="160px"
                      overflowY="auto"
                    >
                      {storeResults.map((s) => (
                        <ListItem
                          key={s.id}
                          px={3}
                          py={2}
                          cursor="pointer"
                          _hover={{ bg: 'blue.50' }}
                          onClick={() => {
                            const label = `${s.branch_code} — ${s.branch_name}`
                            setStoreQuery(label)
                            setStoreResults([])
                            onChange({
                              storeId: String(s.id),
                              storeCode: s.branch_code,
                              storeLabel: label,
                              siteAddress:
                                s.branch_address ||
                                triage.siteAddress ||
                                '',
                            })
                          }}
                        >
                          <Text fontSize="sm" fontWeight="600">
                            {s.branch_code} — {s.branch_name}
                          </Text>
                          {s.branch_address && (
                            <Text fontSize="xs" color="gray.600">
                              {s.branch_address}
                            </Text>
                          )}
                        </ListItem>
                      ))}
                    </List>
                  )}
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm">Site address (if not listed above)</FormLabel>
                  <Input
                    value={triage.siteAddress}
                    onChange={(e) => onChange({ siteAddress: e.target.value })}
                    placeholder="Building, floor, or full address"
                  />
                </FormControl>
              </GridItem>
            </>
          )}

          <GridItem>
            <FormControl>
              <FormLabel fontSize="sm">How is this affecting work?</FormLabel>
              <Textarea
                value={triage.workImpact}
                onChange={(e) => onChange({ workImpact: e.target.value })}
                rows={2}
                placeholder="e.g. Cannot print receipts during peak hours"
              />
            </FormControl>
          </GridItem>

          <GridItem>
            <RadioRow
              label="Others experiencing the same issue?"
              value={triage.othersAffected}
              options={othersAffectedOptions}
              onChange={(v) => onChange({ othersAffected: v })}
            />
          </GridItem>

          <GridItem>
            <RadioRow
              label="Users affected"
              value={triage.userScope}
              options={userScopeOptions}
              onChange={(v) => onChange({ userScope: v })}
            />
          </GridItem>

          <GridItem>
            <RadioRow
              label="Applications affected"
              value={triage.appScope}
              options={appScopeOptions}
              onChange={(v) => onChange({ appScope: v })}
            />
          </GridItem>

          <GridItem>
            <RadioRow
              label="Access type"
              value={triage.accessType}
              options={accessTypeOptions}
              onChange={(v) => onChange({ accessType: v })}
            />
          </GridItem>

          {showOnsite && (
            <>
              <GridItem>
                <RadioRow
                  label="Work permit needed?"
                  value={triage.workPermitNeeded}
                  options={workPermitNeededOptions}
                  onChange={(v) => onChange({ workPermitNeeded: v })}
                />
              </GridItem>

              {triage.workPermitNeeded === 'Yes' && (
                <>
                  <GridItem>
                    <RadioRow
                      label="Permit readily available?"
                      value={triage.permitAvailable}
                      options={permitAvailableOptions}
                      onChange={(v) => onChange({ permitAvailable: v })}
                    />
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel fontSize="sm">Permit processing time</FormLabel>
                      <Input
                        value={triage.permitDuration}
                        onChange={(e) => onChange({ permitDuration: e.target.value })}
                        placeholder="e.g. 2 business days"
                      />
                    </FormControl>
                  </GridItem>
                </>
              )}
            </>
          )}

          <GridItem>
            <RadioRow
              label="Parts replacement approved?"
              value={triage.partsApproved}
              options={partsApprovedOptions}
              onChange={(v) => onChange({ partsApproved: v })}
            />
          </GridItem>

          <GridItem>
            <RadioRow
              label="Close ticket after fix confirmed?"
              value={triage.closeOnFix}
              options={closeOnFixOptions}
              onChange={(v) => onChange({ closeOnFix: v })}
            />
          </GridItem>
        </Grid>
      </Collapse>
    </Box>
  )
}

export const DEFAULT_TRIAGE_STATE: TriageFormState = {
  storeId: '',
  storeCode: '',
  storeLabel: '',
  siteAddress: '',
  errorMessage: '',
  preferredScheduleUntil: '',
  contactAvailabilityText: '',
  workImpact: '',
  othersAffected: '',
  userScope: '',
  appScope: '',
  accessType: '',
  workPermitNeeded: '',
  permitAvailable: '',
  permitDuration: '',
  partsApproved: '',
  closeOnFix: '',
  triageExpanded: false,
}

export function triageFromExtracted(
  extracted: Record<string, unknown> | null | undefined,
  preferredScheduleUntilLocal?: string
): TriageFormState {
  const str = (k: string) => {
    const v = extracted?.[k]
    return v == null ? '' : String(v).trim()
  }
  return {
    ...DEFAULT_TRIAGE_STATE,
    storeId: str('store_id'),
    storeCode: str('store_code'),
    storeLabel: str('store_code')
      ? `${str('store_code')}${str('location') ? ` — ${str('location')}` : ''}`
      : '',
    siteAddress: str('location') || str('site_address'),
    errorMessage: str('error_message'),
    preferredScheduleUntil: preferredScheduleUntilLocal ?? '',
    contactAvailabilityText: str('contact_availability_text') || str('contact_availability'),
    workImpact: str('work_impact'),
    othersAffected: str('others_affected'),
    userScope: str('user_scope'),
    appScope: str('app_scope'),
    accessType: str('access_type'),
    workPermitNeeded: str('work_permit_needed'),
    permitAvailable: str('permit_available'),
    permitDuration: str('permit_duration'),
    partsApproved: str('parts_approved'),
    closeOnFix: str('close_on_fix'),
    triageExpanded: Boolean(str('store_code') || str('work_impact')),
  }
}
